<script>
  import ResponsiveTable from "@/core/components/table/ResponsiveTable"
  import { dateFormat } from "@/core/services/helpers"
  import TariffCoefficientService from "@/domains/lines/services/TariffCoefficientService"
  import Cancel from "@/domains/spl-navegantes/partials/icons/Cancel.vue"
  import Check from "@/domains/spl-navegantes/partials/icons/Check.vue"
  import Pending from "@/domains/spl-navegantes/partials/icons/Pending.vue"
  import { faEye } from "@fortawesome/free-solid-svg-icons"
  import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
  import _isEqual from "lodash/isEqual"
  import ActionsDropdown from "@/core/components/ActionsDropdown"

  const SITUATION_COLORS = {
    1: "#00A86B", // Ativo
    2: "#FF0000", // Inativo
    3: "#FFA500", // Aguardando início
  }

  const SITUATION_LABELS = {
    1: "ATIVO",
    2: "INATIVO",
    3: "AG. INÍCIO DE VIGÊNCIA",
  }

  export default {
    components: {
      ResponsiveTable,
      FontAwesomeIcon,
      Check,
      Cancel,
      Pending,
      ActionsDropdown,
    },
    props: {
      filters: {
        type: Object,
        default: () => ({
          nutransportadora: "",
          dtiniciovalidade: "",
          dtfimvalidade: "",
          flsituacao: "",
        }),
      },
    },
    data() {
      return {
        faEye,
        dateFormat,
        status: "pending",
        loading: false,
        visible: false,
        tariffCoefficients: [],
        tariffCoefficientService: new TariffCoefficientService(),
        headers: [
          { text: "Cód.", value: "idcoeficientetarifa", order: true },
          {
            text: "Transportadora",
            value: "nutransportadora",
            order: true,
          },
          {
            text: "Início da Vigência",
            value: "dtiniciovalidade",
            order: true,
          },
          {
            text: "Fim da Vigência",
            value: "dtfimvalidade",
            order: true,
          },
          { text: "Situação", value: "flstatus", order: true },
          { text: "Ações", value: "", order: false },
        ],
        currentOrder: 1,
        orderOptions: [
          { id: 0, order: { idcoeficientetarifa: "ASC" } },
          { id: 1, order: { idcoeficientetarifa: "DESC" } },
          { id: 2, order: { nutransportadora: "ASC" } },
          { id: 3, order: { nutransportadora: "DESC" } },
          { id: 4, order: { dtiniciovalidade: "ASC" } },
          { id: 5, order: { dtiniciovalidade: "DESC" } },
          { id: 6, order: { dtfimvalidade: "ASC" } },
          { id: 7, order: { dtfimvalidade: "DESC" } },
          { id: 8, order: { flstatus: "ASC" } },
          { id: 9, order: { flstatus: "DESC" } },
        ],
        pagination: {},
        page: 1,
      }
    },
    computed: {
      order() {
        return (
          this.orderOptions.find((i) => i.id === this.currentOrder)?.order || {}
        )
      },
    },
    watch: {
      filters: {
        handler() {
          this.page = 1
          this.searchTariffCoefficients()
        },
        deep: true,
      },
    },
    mounted() {
      this.searchTariffCoefficients()
    },
    methods: {
      async searchTariffCoefficients() {
        this.loading = true
        try {
          const response =
            await this.tariffCoefficientService.searchTariffCoefficients(
              this.filters,
              this.order,
              this.page
            )
          this.tariffCoefficients = response.data
          this.pagination = { ...response, data: undefined }
          this.status = "finished"
        } catch {
          this.status = "error"
        } finally {
          this.loading = false
        }
      },
      editTariff(tariffCoefficient) {
        this.$router.push({
          name: "lines.tariffCoefficient.view",
          params: { id: tariffCoefficient.id },
        })
      },
      dataList(tariffCoefficients) {
        return tariffCoefficients.map((tariffCoefficient) => ({
          id: tariffCoefficient.idcoeficientetarifa,
          cod: tariffCoefficient.idcoeficientetarifa,
          transportadora:
            tariffCoefficient.deter_registry?.organization?.business_name ||
            "N/A",
          inicio: tariffCoefficient.dtiniciovalidade
            ? dateFormat(tariffCoefficient.dtiniciovalidade)
            : "N/A",
          fim: tariffCoefficient.dtfimvalidade
            ? dateFormat(tariffCoefficient.dtfimvalidade)
            : "N/A",
          situacao: tariffCoefficient.flsituacao,
        }))
      },
      changePage(page) {
        if (page !== this.page && this.status !== "pending") {
          this.page = page
          this.searchTariffCoefficients()
        }
      },
      updateSorting(sort) {
        const order = this.orderOptions.find((i) => _isEqual(i.order, sort))
        if (order) {
          this.currentOrder = order.id
          this.reload()
        }
      },
      reload() {
        this.page = 1
        this.searchTariffCoefficients()
      },
      getColorBySituation(situation) {
        return SITUATION_COLORS[situation] || "#000"
      },
      getLabelBySituation(situation) {
        return SITUATION_LABELS[situation] || "DESCONHECIDO"
      },
    },
  }
</script>

<template>
  <div>
    <ResponsiveTable
      :headers="headers"
      :data="dataList(tariffCoefficients)"
      :disabled="loading"
      :pagination="pagination"
      :has-pagination="true"
      :status="status"
      :sorting="true"
      :sorted-header="order"
      @sort="updateSorting"
      @changePage="changePage"
    >
      <template #row="props">
        <td :class="props.tdClass" class="text-xs p-3 w-1/12">
          {{ props.row.cod }}
        </td>
        <td :class="props.tdClass" class="text-xs p-3 w-4/12">
          {{ props.row.transportadora }}
        </td>
        <td :class="props.tdClass" class="text-xs p-3 w-2/12">
          {{ props.row.inicio }}
        </td>
        <td :class="props.tdClass" class="text-xs p-3 w-2/12">
          {{ props.row.fim }}
        </td>
        <td
          :class="props.tdClass"
          :style="{ color: getColorBySituation(props.row.situacao) }"
          class="text-xs p-3 w-full flex items-center gap-2 whitespace-nowrap"
        >
          <div class="h-max">
            <Check v-if="props.row.situacao == 1"/>
            <Cancel v-if="props.row.situacao == 2"/>
            <Pending v-if="props.row.situacao == 3"/>
          </div>
          {{ getLabelBySituation(props.row.situacao) }}
        </td>
        <td :class="props.tdClass" class="w-1/12">
          <div class="flex">
            <div
              class="text-green-base cursor-pointer"
              title="Editar Sessão"
              @click="editTariff(props.row)"
            >
              <FontAwesomeIcon :icon="faEye"/>
            </div>
          </div>
        </td>
      </template>
      <template #responsive="props">
        <tr class="flex items-center py-2">
          <th
            class="w-2/5 flex-shrink-0 text-right pr-4 font-bold text-gray-700 uppercase text-xs"
          >
            CÓD.
          </th>
          <td
            :class="[
                            props.tdClass,
                            'w-3/5',
                            'break-words',
                            'flex',
                            'flex-between',
                            'items-center',
                            'relative'
                        ]"
          >
            <ActionsDropdown class="absolute top-0 right-0">
              <template slot="actions">
                <router-link
                  :to="{
                                        name: 'lines.tariffCoefficient.view',
                                        params: { id: props.row.id }
                                    }"
                  class="block w-full px-4 py-2 text-left text-gray-800 hover:text-white hover:bg-green-500"
                >
                  Ver detalhes
                </router-link>
              </template>
            </ActionsDropdown>
            {{ props.row.cod }}
          </td>
        </tr>
        <tr class="flex items-center py-2">
          <th
            class="w-2/5 flex-shrink-0 text-right pr-4 font-bold text-gray-700 uppercase text-xs"
          >
            Transportadora
          </th>
          <td :class="[props.tdClass, 'w-3/5', 'break-words']">
            {{ props.row.transportadora }}
          </td>
        </tr>
        <tr class="flex items-center py-2">
          <th
            class="w-2/5 flex-shrink-0 text-right pr-4 font-bold text-gray-700 uppercase text-xs"
          >
            Início da Vigência
          </th>
          <td :class="[props.tdClass, 'w-3/5', 'break-words']">
            {{ props.row.inicio }}
          </td>
        </tr>
        <tr class="flex items-center py-2">
          <th
            class="w-2/5 flex-shrink-0 text-right pr-4 font-bold text-gray-700 uppercase text-xs"
          >
            Fim da Vigência
          </th>
          <td :class="[props.tdClass, 'w-3/5', 'break-words']">
            {{ props.row.fim }}
          </td>
        </tr>
        <tr class="flex items-center py-2">
          <th
            class="w-2/5 flex-shrink-0 text-right pr-4 font-bold text-gray-700 uppercase text-xs"
          >
            Situação
          </th>
          <td
            :class="[props.tdClass, 'w-3/5', 'break-words']"
            :style="
                            `color: ${getColorBySituation(props.row.situacao)}`
                        "
          >
            <div class="flex items-center h-full gap-2">
              <div class="h-max">
                <Check v-if="props.row.situacao == 1"/>
                <Cancel v-if="props.row.situacao == 2"/>
                <Pending v-if="props.row.situacao == 3"/>
              </div>
              {{ getLabelBySituation(props.row.situacao) }}
            </div>
          </td>
        </tr>
      </template>
    </ResponsiveTable>
  </div>
</template>
